import { HERO_1_PLAYER_ID, HERO_2_PLAYER_ID } from '../../interaction/keyboard';
import type { ResumePlayerIdentity } from '../../platform/missions';
// Type-only: the cap is asserted in the type system below, so nothing is imported at runtime.
import type { MAX_LOCAL_PLAYERS, PlayerRuntimeState } from '../../shared/types/players';
import type { SessionLaunchConfig } from '../../shared/types/session-launch';
import type { ActivePlayer } from '../shell/services-context';
import { BUILT_IN_LAUNCH_DEFAULTS } from './launch-defaults';

/**
 * Who is in the session, in lane order.
 *
 * The stage builds this to start a mission and Home builds the identity half of it to decide
 * whether to offer "carry on". Both must agree exactly — an offer made against a differently
 * built identity would either withhold a child's own mission or hand them someone else's — so
 * the construction lives here once rather than in each screen.
 *
 * Lanes are fixed for the whole mission: Hero 1 left, Hero 2 right, never swapped (MVP notes §6,
 * COOP-08). A single-player session is the one-entry case of the same shape.
 */

/**
 * A session roster, capped in the type rather than by a runtime check.
 *
 * `MAX_LOCAL_PLAYERS` is 2 and everything downstream assumes it — a lane identifies a player, the
 * resume guard matches slot for slot, the summary lists one row per hero. Expressing the cap as a
 * tuple union means a third player cannot be added here without the return type changing, which
 * is a visible diff rather than a silent widening.
 */
export type SessionRoster = [PlayerRuntimeState] | [PlayerRuntimeState, PlayerRuntimeState];

/**
 * `true` only while the roster shape still honours `MAX_LOCAL_PLAYERS`.
 *
 * Asserted from a test, so widening the tuple without revisiting the platform-wide cap is a
 * compile error rather than a comment nobody re-reads.
 */
export type SessionRosterWithinCap = SessionRoster['length'] extends 1 | typeof MAX_LOCAL_PLAYERS
  ? true
  : never;

export function buildSessionPlayers(
  config: SessionLaunchConfig,
  activePlayer: ActivePlayer | null,
): SessionRoster {
  const label = config.playerLabel ?? activePlayer?.label;

  const hero1: PlayerRuntimeState = {
    playerId: HERO_1_PLAYER_ID,
    lane: 'left',
    band: config.band,
    isGuest: activePlayer?.isGuest ?? true,
    present: true,
    ...(label === undefined ? {} : { shortName: label }),
    ...(activePlayer?.profileId === undefined ? {} : { profileId: activePlayer.profileId }),
  };

  if (config.playerMode !== 'COOP_SIMULTANEOUS_SIMPLE') return [hero1];

  // The mode is the switch, not the presence of a second-player choice: a parent who tried coop
  // and went back to one player must not find a second hero on the stage. A cooperative sitting
  // with nobody named is a guest in the right lane rather than a session with an empty lane.
  const second = config.secondPlayer;
  const hero2: PlayerRuntimeState = {
    playerId: HERO_2_PLAYER_ID,
    lane: 'right',
    band: second?.band ?? config.band,
    isGuest: second?.profileId === undefined,
    present: true,
    ...(second?.label === undefined ? {} : { shortName: second.label }),
    ...(second?.profileId === undefined ? {} : { profileId: second.profileId }),
  };

  return [hero1, hero2];
}

/**
 * The identity half of the above: everything `canResumeMissionProgress` compares, and nothing else.
 *
 * Derived from the roster rather than rebuilt beside it, so the two cannot drift. A sitting with
 * no launch config *and* nothing recorded about who launched is the single-player session it would
 * launch — refusing rather than misattributing.
 *
 * Since D-054(2) both callers pass the config `resolveSitting` returned, which after a reload is
 * the reconstructed sitting rather than the built-in default. So a cooperative mission is offered
 * back to the two children who were playing it, and to nobody else.
 */
export function sessionResumeIdentity(
  config: SessionLaunchConfig | null,
  activePlayer: ActivePlayer | null,
): ResumePlayerIdentity[] {
  return buildSessionPlayers(config ?? BUILT_IN_LAUNCH_DEFAULTS, activePlayer).map((player) => ({
    playerId: player.playerId,
    band: player.band,
    ...(player.profileId === undefined ? {} : { profileId: player.profileId }),
  }));
}
